import { Phone, Mail, Clock, Facebook, Instagram, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import WhatsAppIcon from "./WhatsAppIcon";
import { scrollToId } from "../lib/site";
import { useContent } from "../lib/content";

export default function Footer() {
  const { socials } = useContent();
  const phoneTel = `tel:${(socials.phone || "").replace(/[^+\d]/g, "")}`;
  const waUrl = `https://wa.me/${(socials.whatsapp || "").replace(/\D/g, "")}`;
  const areas = (socials.areas || "").split(",").map((a) => a.trim()).filter(Boolean);

  return (
    <footer id="contact" data-testid="footer" className="bg-reef-dark text-sand-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <img src="/logo-white.png" alt="Reef Deco" className="h-10 w-auto" />
            <p className="mt-4 text-sm text-sand-bg/70 leading-relaxed max-w-xs">
              Premium custom curtains &amp; blinds, professionally measured and installed at your
              doorstep — trusted by homes, apartments, villas, offices &amp; hotels across Dubai, UAE.
            </p>
            <div className="mt-5 flex items-center gap-3">
              <a
                data-testid="footer-facebook-link"
                href={socials.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Reef Deco on Facebook"
                className="w-10 h-10 rounded-full border border-sand-bg/25 flex items-center justify-center text-sand-bg/80 hover:bg-sand-bg hover:text-reef-dark transition-colors"
              >
                <Facebook className="w-4.5 h-4.5" />
              </a>
              <a
                data-testid="footer-instagram-link"
                href={socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Reef Deco on Instagram"
                className="w-10 h-10 rounded-full border border-sand-bg/25 flex items-center justify-center text-sand-bg/80 hover:bg-sand-bg hover:text-reef-dark transition-colors"
              >
                <Instagram className="w-4.5 h-4.5" />
              </a>
              <a
                data-testid="footer-whatsapp-link"
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Chat with Reef Deco on WhatsApp"
                className="w-10 h-10 rounded-full border border-sand-bg/25 flex items-center justify-center text-sand-bg/80 hover:bg-sand-bg hover:text-reef-dark transition-colors"
              >
                <WhatsAppIcon className="w-4.5 h-4.5" />
              </a>
            </div>
          </div>

          <div>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-sand-bg/50 mb-5">Contact</p>
            <ul className="space-y-3.5 text-sm">
              <li>
                <a data-testid="footer-phone-link" href={phoneTel} className="flex items-center gap-3 text-sand-bg/85 hover:text-white transition-colors">
                  <Phone className="w-4 h-4" /> {socials.phone}
                </a>
              </li>
              <li>
                <a data-testid="footer-email-link" href={`mailto:${socials.email}`} className="flex items-center gap-3 text-sand-bg/85 hover:text-white transition-colors">
                  <Mail className="w-4 h-4" /> {socials.email}
                </a>
              </li>
              <li>
                <a data-testid="footer-whatsapp-text-link" href={waUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sand-bg/85 hover:text-white transition-colors">
                  <WhatsAppIcon className="w-4 h-4" /> WhatsApp us
                </a>
              </li>
              {socials.google_maps && (
                <li>
                  <a data-testid="footer-googlemaps-link" href={socials.google_maps} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sand-bg/85 hover:text-white transition-colors">
                    <MapPin className="w-4 h-4" /> Find us on Google Maps
                  </a>
                </li>
              )}
              <li className="flex items-center gap-3 text-sand-bg/85">
                <Clock className="w-4 h-4" /> {socials.hours}
              </li>
            </ul>
          </div>

          <div>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-sand-bg/50 mb-5">Quick links</p>
            <ul className="space-y-3 text-sm">
              {[["process", "How it works"], ["collections", "Collections"], ["estimator", "Price estimator"], ["book", "Book a free visit"], ["faq", "FAQ"], ["reviews", "Reviews"]].map(([id, label]) => (
                <li key={id}>
                  <button
                    data-testid={`footer-link-${id}`}
                    onClick={() => scrollToId(id)}
                    className="text-sand-bg/85 hover:text-white transition-colors"
                  >
                    {label}
                  </button>
                </li>
              ))}
              <li>
                <Link to="/blog" data-testid="footer-link-blog" className="text-sand-bg/85 hover:text-white transition-colors">
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-sand-bg/50 mb-5">Areas we serve in Dubai</p>
            <div className="flex flex-wrap gap-2">
              {areas.map((a) => (
                <span key={a} className="px-3.5 py-1.5 rounded-full border border-sand-bg/20 text-sand-bg/75 text-xs">
                  {a}
                </span>
              ))}
            </div>
          </div>
        </div>

        {(socials.google_maps || "").includes("embed") && (
          <div className="mt-12 rounded-2xl overflow-hidden border border-sand-bg/15 aspect-[21/9]">
            <iframe
              src={socials.google_maps}
              title="Reef Deco location map"
              data-testid="footer-map-embed"
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        )}

        <div className="mt-14 pt-7 border-t border-sand-bg/15 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-mono text-[11px] tracking-[0.15em] uppercase text-sand-bg/50">
            © 2026 Reef Deco — Custom Curtains &amp; Blinds, Dubai, UAE
          </p>
          <Link
            to="/admin"
            data-testid="admin-dashboard-link"
            className="font-mono text-[11px] tracking-[0.15em] uppercase text-sand-bg/40 hover:text-sand-bg transition-colors"
          >
            Team login
          </Link>
        </div>
      </div>
    </footer>
  );
}
